import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Save, 
  Upload,
  Image,
  Type,
  Layout,
  Eye,
  Loader2,
  Plus,
  Trash2,
  GripVertical
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useListKeys() {
  const lists = useRef({});
  const nextId = useRef(0);
  const keysFor = (name, length) => {
    const arr = (lists.current[name] ||= []);
    while (arr.length < length) arr.push(`${name}-${nextId.current++}`);
    return arr;
  };
  const removeKey = (name, index) => lists.current[name]?.splice(index, 1);
  return { keysFor, removeKey };
}

export default function AdminSiteEditor() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);
  const { keysFor, removeKey } = useListKeys();
  
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageTarget, setImageTarget] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/site-content`, { headers: getAuthHeaders() });
      setContent(response.data.content || response.data);
    } catch (error) {
      toast.error('Failed to load site content');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/site-content`, content, { headers: getAuthHeaders() });
      toast.success('Site content saved successfully!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !imageTarget) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('section', imageTarget.section);

    try {
      const response = await axios.post(
        `${API_URL}/admin/site-content/upload-image`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Update content with new image URL
      const newContent = { ...content };
      if (imageTarget.section === 'hero') {
        newContent.hero.image_url = `${API_URL}/site-images/${response.data.id}`;
      } else if (imageTarget.section === 'about') {
        newContent.about.image_url = `${API_URL}/site-images/${response.data.id}`;
      }
      setContent(newContent);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      setImageTarget(null);
    }
  };

  const triggerImageUpload = (section) => {
    setImageTarget({ section });
    fileInputRef.current?.click();
  };

  const updateHero = (field, value) => {
    setContent(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  const updateAbout = (field, value) => {
    setContent(prev => ({
      ...prev,
      about: { ...prev.about, [field]: value }
    }));
  };

  const updateBenefit = (index, value) => {
    setContent(prev => {
      const newBenefits = [...prev.about.benefits];
      newBenefits[index] = value;
      return {
        ...prev,
        about: { ...prev.about, benefits: newBenefits }
      };
    });
  };

  const addBenefit = () => {
    setContent(prev => ({
      ...prev,
      about: { ...prev.about, benefits: [...prev.about.benefits, 'New benefit'] }
    }));
  };

  const removeBenefit = (index) => {
    removeKey('benefits', index);
    setContent(prev => ({
      ...prev,
      about: { ...prev.about, benefits: prev.about.benefits.filter((_, i) => i !== index) }
    }));
  };

  const updateService = (index, field, value) => {
    setContent(prev => {
      const newServices = [...prev.services];
      newServices[index] = { ...newServices[index], [field]: value };
      return { ...prev, services: newServices };
    });
  };

  const updateRequirement = (index, field, value) => {
    setContent(prev => {
      const newReqs = [...prev.requirements];
      newReqs[index] = { ...newReqs[index], [field]: value };
      return { ...prev, requirements: newReqs };
    });
  };

  const updateCta = (field, value) => {
    setContent(prev => ({
      ...prev,
      cta: { ...prev.cta, [field]: value }
    }));
  };

  const updateFooter = (field, value) => {
    setContent(prev => ({
      ...prev,
      footer: { ...prev.footer, [field]: value }
    }));
  };

  const updateFooterService = (index, value) => {
    setContent(prev => {
      const newServices = [...(prev.footer?.services || [])];
      newServices[index] = value;
      return {
        ...prev,
        footer: { ...prev.footer, services: newServices }
      };
    });
  };

  const addFooterService = () => {
    setContent(prev => ({
      ...prev,
      footer: { 
        ...prev.footer, 
        services: [...(prev.footer?.services || []), 'New Service'] 
      }
    }));
  };

  const removeFooterService = (index) => {
    removeKey('footerServices', index);
    setContent(prev => ({
      ...prev,
      footer: { 
        ...prev.footer, 
        services: (prev.footer?.services || []).filter((_, i) => i !== index) 
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="own-type-scale min-h-screen bg-[#0A0A0F]">
      {/* Header */}
      <header className="bg-[#12121A] border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="text-slate-400 hover:text-white px-2 sm:px-4 flex-shrink-0"
                data-testid="back-to-admin"
              >
                <ArrowLeft size={18} className="sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-white truncate" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Site Editor
                </h1>
                <p className="text-sm text-slate-500 hidden sm:block">Edit homepage content</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <a href="/" target="_blank" rel="noopener noreferrer" data-testid="preview-site-link">
                <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 px-3 sm:px-4">
                  <Eye size={18} className="sm:mr-2" />
                  <span className="hidden sm:inline">Preview</span>
                </Button>
              </a>
              <Button
                onClick={saveContent}
                disabled={saving}
                className="btn-gradient text-white border-0 px-3 sm:px-4"
                data-testid="save-content-btn"
              >
                {saving ? <Loader2 className="animate-spin sm:mr-2" size={18} /> : <Save size={18} className="sm:mr-2" />}
                <span className="hidden sm:inline">Save Changes</span>
                <span className="sm:hidden ml-1">Save</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-28 sm:pb-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-48 flex-shrink-0">
            <nav className="flex lg:flex-col gap-2 lg:gap-1 overflow-x-auto pb-1 lg:pb-0 lg:sticky lg:top-24 -mx-3 px-3 sm:mx-0 sm:px-0">
              {[
                { id: 'hero', label: 'Hero Section', icon: Layout },
                { id: 'services', label: 'Services', icon: Type },
                { id: 'about', label: 'About Section', icon: Image },
                { id: 'requirements', label: 'Requirements', icon: Type },
                { id: 'cta', label: 'Call to Action', icon: Type },
                { id: 'footer', label: 'Footer', icon: Layout }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 rounded-lg text-left whitespace-nowrap transition-colors border ${
                    activeSection === id 
                      ? 'bg-violet-500/20 text-violet-300 border-violet-500/30' 
                      : 'text-slate-400 border-slate-800 lg:border-transparent hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`nav-${id}`}
                >
                  <Icon size={18} />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />

            {/* Hero Section Editor */}
            {activeSection === 'hero' && content?.hero && (
              <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 animate-fade-in">
                <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Hero Section
                </h2>

                <div className="space-y-6">
                  {/* Hero Image */}
                  <div>
                    <Label className="text-slate-300 mb-2 block">Hero Image</Label>
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      <div className="w-full sm:w-48 h-40 sm:h-32 rounded-lg overflow-hidden border border-slate-700 bg-slate-800 flex-shrink-0">
                        <img 
                          src={content.hero.image_url} 
                          alt="Hero" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 w-full min-w-0">
                        <Button
                          variant="outline"
                          onClick={() => triggerImageUpload('hero')}
                          disabled={uploadingImage}
                          className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        >
                          {uploadingImage ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
                          Upload New Image
                        </Button>
                        <p className="text-xs text-slate-500 mt-2">Or paste an image URL:</p>
                        <Input
                          value={content.hero.image_url}
                          onChange={(e) => updateHero('image_url', e.target.value)}
                          placeholder="https://..."
                          className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Badge Text */}
                  <div>
                    <Label className="text-slate-300">Badge Text</Label>
                    <Input
                      value={content.hero.badge_text}
                      onChange={(e) => updateHero('badge_text', e.target.value)}
                      className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      data-testid="hero-badge"
                    />
                  </div>

                  {/* Title */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Title Line 1</Label>
                      <Input
                        value={content.hero.title_line1}
                        onChange={(e) => updateHero('title_line1', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                        data-testid="hero-title1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Title Line 2 (Gradient)</Label>
                      <Input
                        value={content.hero.title_line2}
                        onChange={(e) => updateHero('title_line2', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                        data-testid="hero-title2"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Textarea
                      value={content.hero.description}
                      onChange={(e) => updateHero('description', e.target.value)}
                      rows={3}
                      className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      data-testid="hero-description"
                    />
                  </div>

                  {/* CTA Buttons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Primary Button Text</Label>
                      <Input
                        value={content.hero.cta_primary}
                        onChange={(e) => updateHero('cta_primary', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Secondary Button Text</Label>
                      <Input
                        value={content.hero.cta_secondary}
                        onChange={(e) => updateHero('cta_secondary', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Services Section Editor */}
            {activeSection === 'services' && content?.services && (
              <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 animate-fade-in">
                <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Services Section
                </h2>

                <div className="space-y-6">
                  {content.services.map((service, index) => (
                    <div key={keysFor('services', content.services.length)[index]} className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                      <div className="flex items-center gap-2 mb-4">
                        <GripVertical size={16} className="text-slate-600" />
                        <span className="text-sm font-semibold text-violet-400">Service {index + 1}</span>
                      </div>
                      <div className="grid gap-4">
                        <div>
                          <Label className="text-slate-300">Title</Label>
                          <Input
                            value={service.title}
                            onChange={(e) => updateService(index, 'title', e.target.value)}
                            className="mt-2 bg-[#12121A] border-slate-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Description</Label>
                          <Textarea
                            value={service.description}
                            onChange={(e) => updateService(index, 'description', e.target.value)}
                            rows={2}
                            className="mt-2 bg-[#12121A] border-slate-700 text-white"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* About Section Editor */}
            {activeSection === 'about' && content?.about && (
              <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 animate-fade-in">
                <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                  About Section
                </h2>

                <div className="space-y-6">
                  {/* About Image */}
                  <div>
                    <Label className="text-slate-300 mb-2 block">About Image</Label>
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      <div className="w-full sm:w-48 h-40 sm:h-32 rounded-lg overflow-hidden border border-slate-700 bg-slate-800 flex-shrink-0">
                        <img 
                          src={content.about.image_url} 
                          alt="About" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 w-full min-w-0">
                        <Button
                          variant="outline"
                          onClick={() => triggerImageUpload('about')}
                          disabled={uploadingImage}
                          className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        >
                          {uploadingImage ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
                          Upload New Image
                        </Button>
                        <p className="text-xs text-slate-500 mt-2">Or paste an image URL:</p>
                        <Input
                          value={content.about.image_url}
                          onChange={(e) => updateAbout('image_url', e.target.value)}
                          placeholder="https://..."
                          className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Title Line 1</Label>
                      <Input
                        value={content.about.title_line1}
                        onChange={(e) => updateAbout('title_line1', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Title Line 2 (Gradient)</Label>
                      <Input
                        value={content.about.title_line2}
                        onChange={(e) => updateAbout('title_line2', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Textarea
                      value={content.about.description}
                      onChange={(e) => updateAbout('description', e.target.value)}
                      rows={4}
                      className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                    />
                  </div>

                  {/* Benefits */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-slate-300">Benefits List</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={addBenefit}
                        className="text-violet-400 hover:text-violet-300"
                      >
                        <Plus size={16} className="mr-1" />
                        Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {content.about.benefits.map((benefit, index) => (
                        <div key={keysFor('benefits', content.about.benefits.length)[index]} className="flex gap-2">
                          <Input
                            value={benefit}
                            onChange={(e) => updateBenefit(index, e.target.value)}
                            className="bg-[#0A0A0F] border-slate-700 text-white"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeBenefit(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Requirements Section Editor */}
            {activeSection === 'requirements' && content?.requirements && (
              <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 animate-fade-in">
                <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Requirements Section
                </h2>

                <div className="space-y-6">
                  {content.requirements.map((req, index) => (
                    <div key={keysFor('requirements', content.requirements.length)[index]} className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                      <div className="flex items-center gap-2 mb-4">
                        <GripVertical size={16} className="text-slate-600" />
                        <span className="text-sm font-semibold text-violet-400">Requirement {index + 1}</span>
                      </div>
                      <div className="grid gap-4">
                        <div>
                          <Label className="text-slate-300">Title</Label>
                          <Input
                            value={req.title}
                            onChange={(e) => updateRequirement(index, 'title', e.target.value)}
                            className="mt-2 bg-[#12121A] border-slate-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Description</Label>
                          <Textarea
                            value={req.description}
                            onChange={(e) => updateRequirement(index, 'description', e.target.value)}
                            rows={2}
                            className="mt-2 bg-[#12121A] border-slate-700 text-white"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Section Editor */}
            {activeSection === 'cta' && content?.cta && (
              <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 animate-fade-in">
                <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Call to Action Section
                </h2>

                <div className="space-y-6">
                  {/* Title */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Title Line 1</Label>
                      <Input
                        value={content.cta.title_line1}
                        onChange={(e) => updateCta('title_line1', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Title Line 2 (Gradient)</Label>
                      <Input
                        value={content.cta.title_line2}
                        onChange={(e) => updateCta('title_line2', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Textarea
                      value={content.cta.description}
                      onChange={(e) => updateCta('description', e.target.value)}
                      rows={3}
                      className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                    />
                  </div>

                  {/* Button Text */}
                  <div>
                    <Label className="text-slate-300">Button Text</Label>
                    <Input
                      value={content.cta.button_text}
                      onChange={(e) => updateCta('button_text', e.target.value)}
                      className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Footer Section Editor */}
            {activeSection === 'footer' && (
              <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 animate-fade-in">
                <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Footer Section
                </h2>

                <div className="space-y-6">
                  {/* Company Description */}
                  <div>
                    <Label className="text-slate-300">Company Description</Label>
                    <Textarea
                      value={content.footer?.description || ''}
                      onChange={(e) => updateFooter('description', e.target.value)}
                      rows={3}
                      placeholder="Brief description of your company..."
                      className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      data-testid="footer-description"
                    />
                  </div>

                  {/* Contact Information */}
                  <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                    <h3 className="text-sm font-semibold text-violet-400 mb-4">Contact Information</h3>
                    <div className="grid gap-4">
                      <div>
                        <Label className="text-slate-300">Phone Number</Label>
                        <Input
                          value={content.footer?.phone || ''}
                          onChange={(e) => updateFooter('phone', e.target.value)}
                          placeholder="1-800-EXAMPLE"
                          className="mt-2 bg-[#12121A] border-slate-700 text-white"
                          data-testid="footer-phone"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Email Address</Label>
                        <Input
                          value={content.footer?.email || ''}
                          onChange={(e) => updateFooter('email', e.target.value)}
                          placeholder="email@example.com"
                          className="mt-2 bg-[#12121A] border-slate-700 text-white"
                          data-testid="footer-email"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Location / Coverage</Label>
                        <Input
                          value={content.footer?.location || ''}
                          onChange={(e) => updateFooter('location', e.target.value)}
                          placeholder="Nationwide Coverage"
                          className="mt-2 bg-[#12121A] border-slate-700 text-white"
                          data-testid="footer-location"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section Titles */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Quick Links Title</Label>
                      <Input
                        value={content.footer?.quick_links_title || 'Quick Links'}
                        onChange={(e) => updateFooter('quick_links_title', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Services Title</Label>
                      <Input
                        value={content.footer?.services_title || 'We Install'}
                        onChange={(e) => updateFooter('services_title', e.target.value)}
                        className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      />
                    </div>
                  </div>

                  {/* Services List */}
                  <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-violet-400">Services List</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={addFooterService}
                        className="text-violet-400 hover:text-violet-300"
                      >
                        <Plus size={16} className="mr-1" />
                        Add
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {(content.footer?.services || []).map((service, index, list) => (
                        <div key={keysFor('footerServices', list.length)[index]} className="flex gap-2">
                          <GripVertical size={16} className="text-slate-600 mt-3 flex-shrink-0" />
                          <Input
                            value={service}
                            onChange={(e) => updateFooterService(index, e.target.value)}
                            className="bg-[#12121A] border-slate-700 text-white"
                            data-testid={`footer-service-${index}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFooterService(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Copyright */}
                  <div>
                    <Label className="text-slate-300">Copyright Name</Label>
                    <Input
                      value={content.footer?.copyright || ''}
                      onChange={(e) => updateFooter('copyright', e.target.value)}
                      placeholder="Company Name"
                      className="mt-2 bg-[#0A0A0F] border-slate-700 text-white"
                      data-testid="footer-copyright"
                    />
                    <p className="text-xs text-slate-500 mt-1">This will appear as "© 2025 [Your Name]. All rights reserved."</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
